<script setup lang="ts">
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { getCurrentLocale } from '@/i18n';
import { EN_FORMAT_CURRENCY, FR_FORMAT_CURRENCY } from '@/constants';
import { useUserAuthorizationsStore } from '@/store/UserAuthorizationsStore';
import Button from 'primevue/button';
import Checkbox from 'primevue/checkbox';
import DataTable from 'primevue/datatable';
import InputNumber from 'primevue/inputnumber';
import Select from 'primevue/select';
import TableColumn from 'primevue/column';
import { useClientCopyComposable } from '@/components/modal/clientCopy/useClientCopyComposable';

const props = defineProps<{
  fareAvailableData: any[];
  classesMargin: any[];
  listPassenger: any[];
  isShowCharter: boolean;
}>();

const emits = defineEmits<{
  classSelected: [value: any];
  handleCharter: [];
  numberOfPassengersFares: [value: any];
  selectMarginPrices: [value: any];
  selectMargins: [value: any];
}>();

const { t } = useI18n({ useScope: 'global' });

const columns = ref([
  { field: 'type', header: t('worksheet.type') },
  { field: 'fareFamilyName', header: t('worksheet.fare_families') },
  { field: 'fareBasis', header: t('worksheet.fare_basis') },
  { field: 'published', header: t('worksheet.published') },
  { field: 'groupsB', header: t('worksheet.groupsb') },
  { field: 'vac', header: t('worksheet.vac') },
  { field: 'charter', header: t('worksheet.charter') },
  { field: 'number_of_passengers', header: t('worksheet.number_of_passengers') },
  { field: 'margin', header: t('worksheet.margin') },
  { field: 'select', header: t('common.select') }
]);

const currentLanguage = getCurrentLocale();
const userAuthorizationsStore = useUserAuthorizationsStore();
const authorizations: any = userAuthorizationsStore.permissionByRole;
const { formatCurrency } = useClientCopyComposable(currentLanguage, currentLanguage === 'en' ? EN_FORMAT_CURRENCY : FR_FORMAT_CURRENCY);
const numberOfPassengersFares = ref(Array(props.fareAvailableData.length).fill(1));
const selectMargins = ref(Array(props.fareAvailableData.length).fill(props.classesMargin[0].key));
const selectMarginPrices = ref(Array(props.fareAvailableData.length).fill(0));
const classSelected = ref([]);

if (!authorizations?.worksheet_air_only?.charter_pricing_view || !props.isShowCharter) {
  columns.value.splice(6, 1);
}

const tooltipPublished = (data: any) => {
  const currencyBase = data?.basePublishedPrice ? formatCurrency(Number(data?.basePublishedPrice)) : formatCurrency('0.00');
  const currencyTaxes = formatCurrency(Number(data?.published) - Number(data?.basePublishedPrice));
  return `${t('worksheet.base')}: ${currencyTaxes === 'N/A' || currencyTaxes === 'S.0' ? formatCurrency('0.00') : currencyBase}
  ${t('worksheet.taxes')}: ${currencyTaxes === 'N/A' || currencyTaxes === 'S.0' ? formatCurrency('0.00') : currencyTaxes}`;
};

const tooltipGroupsB = (data: any) => {
  const currencyBase = data?.baseGroupsBPrice ? formatCurrency(Number(data?.baseGroupsBPrice)) : formatCurrency('0.00');
  const currencyTaxes = formatCurrency(Number(data?.groupsB) - Number(data?.baseGroupsBPrice));
  return `${t('worksheet.base')}: ${currencyTaxes === 'N/A' || currencyTaxes === 'S.0' ? formatCurrency('0.00') : currencyBase}
  ${t('worksheet.taxes')}: ${currencyTaxes === 'N/A' || currencyTaxes === 'S.0' ? formatCurrency('0.00') : currencyTaxes}`;
};

const tooltipVac = (data: any) => {
  const currencyBase = data?.baseVacPrice ? formatCurrency(Number(data?.baseVacPrice)) : formatCurrency('0.00');
  const currencyTaxes = formatCurrency(Number(data?.vac) - Number(data?.baseVacPrice));
  return `${t('worksheet.base')}: ${currencyTaxes === 'N/A' || currencyTaxes === 'S.0' ? formatCurrency('0.00') : currencyBase}
  ${t('worksheet.taxes')}: ${currencyTaxes === 'N/A' || currencyTaxes === 'S.0' ? formatCurrency('0.00') : currencyTaxes}`;
};

const handleCharter = () => {
  emits('handleCharter');
};

watch(
  () => classSelected.value,
  (newVal) => {
    emits('classSelected', newVal);
  },
  { deep: true }
);

watch(
  numberOfPassengersFares.value,
  (newVal) => {
    emits('numberOfPassengersFares', newVal);
  },
  { deep: true, immediate: true }
);

watch(
  selectMarginPrices.value,
  (newVal) => {
    emits('selectMarginPrices', newVal);
  },
  { deep: true, immediate: true }
);

watch(
  selectMargins.value,
  (newVal) => {
    emits('selectMargins', newVal);
  },
  { deep: true, immediate: true }
);

const updateSelectMarginPrices = (index: number) => {
  if (!selectMarginPrices.value[index] || !Number(selectMarginPrices.value[index])) {
    selectMarginPrices.value[index] = 0;
  }
};

const checkDisabled = (index: number) => {
  return classSelected.value.length >= 5 && !classSelected.value.find((item) => item.index === index);
};

const calculatePublished = (data: any) => {
  const calculatePublished = Number(data?.published);
  return new Intl.NumberFormat(currentLanguage, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(calculatePublished);
};

const calculateGroupsB = (data: any) => {
  const calculateGroupsB = Number(data?.groupsB);
  return new Intl.NumberFormat(currentLanguage, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(calculateGroupsB);
};

const calculateVac = (data: any) => {
  const calculateVac = Number(data?.vac);
  return new Intl.NumberFormat(currentLanguage, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(calculateVac);
};

const calculateCharter = (data: any) => {
  const calculateCharter = Number(data?.charter);
  return new Intl.NumberFormat(currentLanguage, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(calculateCharter);
};
</script>

<template>
  <DataTable editMode="cell" :value="fareAvailableData" rowGroupMode="subheader" groupRowsBy="type" sortMode="single" sortField="type" :sortOrder="-1" scrollable>
    <template #empty>{{ $t('worksheet.fare_not_available') }}</template>
    <TableColumn v-for="column of columns" :key="column.field" :field="column.field">
      <template #header>
        <span :class="{ 'flex justify-content-center gap-3 align-content-center w-full': column.field === 'margin' }">
          {{ column.header }}
        </span>
      </template>
      <template #body="{ data, field, index }">
        <div :class="checkDisabled(index) ? 'pointer-events-none opacity-50' : ''">
          <span v-if="column.field === 'type' || column.field === 'fareFamilyName' || column.field === 'fareBasis'">
            {{ data[field] }}
          </span>
          <span v-if="column.field === 'published'" class="long-text" v-tooltip="tooltipPublished(data)">
            {{ calculatePublished(data, field, index) }}
          </span>
          <span v-if="column.field === 'groupsB'" class="long-text" v-tooltip="tooltipGroupsB(data)">
            {{ calculateGroupsB(data, field, index) }}
          </span>
          <span v-if="column.field === 'vac'" class="long-text" v-tooltip="tooltipVac(data)">
            {{ calculateVac(data, field, index) }}
          </span>
          <span v-if="column.field === 'charter'">
            {{ calculateCharter(data, field, index) }}
          </span>
          <span v-if="column.field === 'number_of_passengers'">
            <Select v-model="numberOfPassengersFares[index]" :options="listPassenger" optionLabel="value" optionValue="value" class="w-6rem" />
          </span>
          <div v-if="column.field === 'margin'" class="flex justify-content-center">
            <div class="w-8rem">
              <Select v-model="selectMargins[index]" class="capitalize w-full" :options="classesMargin" optionLabel="value" optionValue="key">
                <template #option="slotProps">
                  <p class="capitalize">
                    {{ slotProps.option.value }}
                  </p>
                </template>
              </Select>
            </div>
            <div class="w-6rem ml-3">
              <InputNumber
                class="w-full input_margin_fare"
                :max="9999.99"
                :min="0"
                :minFractionDigits="2"
                :maxFractionDigits="2"
                :allowEmpty="false"
                :locale="currentLanguage == 'fr' ? 'fr-FR' : 'en-US'"
                v-model="selectMarginPrices[index]"
                @blur="updateSelectMarginPrices(index)"
              />
            </div>
          </div>
          <p v-if="column.field === 'select'" class="flex">
            <Checkbox v-model="classSelected" name="classSelected" :value="{ data, index }" :disabled="checkDisabled(index)" />
          </p>
        </div>
      </template>
    </TableColumn>
    <template #groupheader="slotProps">
      <div class="flex align-items-center gap-2">
        <span class="text-base font-bold text-900">{{ slotProps.data.type }} {{ $t('common.class') }}</span>
        <div v-if="authorizations?.worksheet_air_only?.charter_pricing_view && isShowCharter" class="flex align-items-center justify-content-end width-80">
          <Button id="fare-charter-btn" :disabled="!authorizations.worksheet_air_only.charter_pricing_view" :label="$t('worksheet.charter')" @click="handleCharter()" />
        </div>
      </div>
    </template>
  </DataTable>
</template>
<style lang="scss">
.input_margin_fare {
  .p-inputtext.p-component {
    max-width: 6rem !important;
  }
}
</style>
